import {
  Checkbox,
  CrudMode,
  DataDisplay,
  formatBoolean,
  formatPhoneNumber,
  Grid,
  GridBreak,
  pn,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useDebounce,
  useElementBreakpoints,
  ValidationErrors,
  IconButton,
  Chip,
} from "@compass/core";
import { AddressFields, Input, Select } from "components";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Address,
  LkStudentParentDisplayFieldId,
  Parent,
  ParentAssignDto,
  StudentParent,
  StudentParentEditResource,
  StudentParentViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { PhoneNumberInput } from "components/common/PhoneNumberInput";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { parentTextingOptInPageRoute } from "pages/students/student-parents/StudentParentPage/StudentParentTextingOptInPage";
import { useRouter } from "hooks";

export const StudentParentPageBody = (props: {
  crudMode: CrudMode;
  studentParent: StudentParent;
  setStudentParent: (value: StudentParent) => void;
  viewResource: StudentParentViewResource;
  editResource: StudentParentEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const debouncedFirstName = useDebounce(props.studentParent.parent?.firstName);
  const debouncedLastName = useDebounce(props.studentParent.parent?.lastName);
  const [assignableParents, setAssignableParents] = useState<ParentAssignDto[]>([]);
  const isAssigningParent = !props.studentParent.id && props.studentParent.parentId > 0;
  const properties = props.viewResource.properties || [];
  const parentProperties = properties.find((x) => x.propertyName === pn<StudentParent>("parent"))?.children || [];
  const parentValidationErrors = props.validationErrors[pn<StudentParent>("parent")] || [];

  const displayFields = props.viewResource.displayFields || [];
  const relationDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.Relation);
  const parentIdDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.ParentId);
  const addressDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.Address);
  const languageDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.Language);
  const educationLevelDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.EducationLevel);
  const secondaryEmailDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.SecondaryEmail);
  const phoneNumberDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.PhoneNumber);
  const cellPhoneDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.CellPhone);
  const workPhoneDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.WorkPhone);
  const workExtensionDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.WorkExtension);
  const noteDisplayField = displayFields.find((x) => x.id === LkStudentParentDisplayFieldId.Note);

  const { matchTemplatePath } = useRouter();

  const setAddressFromStudent = (studentId: number | undefined) => {
    if (studentId) {
      ApiService.studentParents.getStudentAddress(studentId).then((response) => {
        props.setStudentParent(
          produce(props.studentParent, (draft) => {
            draft.parent!.address = { ...response } as Address;
          })
        );
      });
    }
  };

  useEffect(() => {
    if (!props.studentParent.id) {
      props.setStudentParent(
        produce(props.studentParent, (draft) => {
          draft.parentId = 0;
        })
      );

      if (debouncedFirstName && debouncedLastName) {
        ApiService.studentParents.getParentsByName(debouncedFirstName, debouncedLastName).then((response) => {
          setAssignableParents(response);
        });
      } else {
        setAssignableParents([]);
      }
    }
  }, [debouncedFirstName, debouncedLastName]);

  const handleReturnToNewParent = () => {
    props.setStudentParent(
      produce(props.studentParent, (draft) => {
        draft.parentId = 0;
        draft.parent = {
          firstName: draft.parent?.firstName,
          lastName: draft.parent?.lastName,
        } as Parent;
      })
    );
  };

  const handleParentSelect = (_e: React.MouseEvent, value: number | null) => {
    if (value === null) {
      handleReturnToNewParent();
      return;
    }

    props.setStudentParent(
      produce(props.studentParent, (draft) => {
        draft.parentId = value;
        const assignToParent = assignableParents.find((x) => x.parent?.id === value);
        draft.parent = {
          ...assignToParent,
          firstName: draft.parent?.firstName!,
          lastName: draft.parent?.lastName!,
        } as Parent;
      })
    );
  };

  const relationField = () => {
    const propertyName = pn<StudentParent>("relationId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.studentParent.relation?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.relations?.find((x) => x.id === props.studentParent.relationId)}
        options={props.editResource.relations}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              draft.relationId = value?.id;
            })
          )
        }
      />
    );
  };

  const lastNameField = () => {
    const propertyName = pn<Parent>("lastName");
    const property = parentProperties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.studentParent.parent?.lastName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={parentValidationErrors?.[propertyName]}
        value={props.studentParent.parent?.lastName}
        onChange={(e) =>
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              draft.parent!.lastName = e.target.value;
            })
          )
        }
      />
    );
  };

  const firstNameField = () => {
    const propertyName = pn<Parent>("firstName");
    const property = parentProperties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.studentParent.parent?.firstName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={parentValidationErrors?.[propertyName]}
        value={props.studentParent.parent?.firstName}
        onChange={(e) =>
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              draft.parent!.firstName = e.target.value;
            })
          )
        }
      />
    );
  };

  const languageField = () => {
    const propertyName = pn<Parent>("languageId");
    const property = parentProperties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.studentParent.parent?.language?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={parentValidationErrors?.[propertyName]}
        value={props.editResource.languages?.find((x) => x.id === props.studentParent.parent?.languageId)}
        options={props.editResource.languages}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              draft.parent!.languageId = value?.id;
            })
          )
        }
      />
    );
  };

  const educationLevelField = () => {
    const propertyName = pn<Parent>("educationLevelId");
    const property = parentProperties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.studentParent.parent?.educationLevel?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={parentValidationErrors?.[propertyName]}
        value={props.editResource.educationLevels?.find((x) => x.id === props.studentParent.parent?.educationLevelId)}
        options={props.editResource.educationLevels}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              draft.parent!.educationLevelId = value?.id;
            })
          )
        }
      />
    );
  };

  const emailField = () => {
    const propertyName = pn<Parent>("email");
    const property = parentProperties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.studentParent.parent?.email}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={parentValidationErrors?.[propertyName]}
        value={props.studentParent.parent?.email}
        onChange={(e) =>
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              draft.parent!.email = e.target.value;
            })
          )
        }
      />
    );
  };

  const secondaryEmailField = () => {
    const propertyName = pn<Parent>("secondaryEmail");
    const property = parentProperties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.studentParent.parent?.secondaryEmail}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={parentValidationErrors?.[propertyName]}
        value={props.studentParent.parent?.secondaryEmail}
        onChange={(e) =>
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              draft.parent!.secondaryEmail = e.target.value;
            })
          )
        }
      />
    );
  };

  const phoneNumberField = () => {
    const propertyName = pn<Parent>("phoneNumber");
    const property = parentProperties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {formatPhoneNumber(props.studentParent.parent?.phoneNumber)}
      </DataDisplay>
    ) : (
      <PhoneNumberInput
        modelProperty={property}
        errors={parentValidationErrors?.[propertyName]}
        value={props.studentParent.parent?.phoneNumber}
        updatePhoneNumber={(p) => {
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              if (!draft.parent) {
                draft.parent = {} as Parent;
              }
              draft.parent.phoneNumber = p;
            })
          )
        }}
      />
    );
  };

  const navigate = useNavigate();

  const cellPhoneField = () => {
    const propertyName = pn<Parent>("cellPhone");
    const property = parentProperties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="inherit">{formatPhoneNumber(props.studentParent.parent?.cellPhone)}</Typography>
          {props.studentParent.parent?.cellPhone && (
            <IconButton
              color="secondary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  parentTextingOptInPageRoute.getPath({
                    parentId: props.studentParent.parentId,
                    screenType: "dialog",
                  })
                );
              }}
            >
              <PhoneIphoneIcon />
            </IconButton>
          )}
        </Stack>
      </DataDisplay>
    ) : (
      <PhoneNumberInput
        modelProperty={property}
        errors={parentValidationErrors?.[propertyName]}
        value={props.studentParent.parent?.cellPhone}
        updatePhoneNumber={(p) => {
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              if (!draft.parent) {
                draft.parent = {} as Parent;
              }
              draft.parent.cellPhone = p;
            })
          )
        }}
      />
    );
  };

  const workPhoneField = () => {
    const propertyName = pn<Parent>("workPhone");
    const property = parentProperties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {formatPhoneNumber(props.studentParent.parent?.workPhone)}
      </DataDisplay>
    ) : (
      <PhoneNumberInput
        modelProperty={property}
        errors={parentValidationErrors?.[propertyName]}
        value={props.studentParent.parent?.workPhone}
        updatePhoneNumber={(p) => {
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              if (!draft.parent) {
                draft.parent = {} as Parent;
              }
              draft.parent.workPhone = p;
            })
          )
        }}
      />
    );
  };

  const workExtensionField = () => {
    const propertyName = pn<Parent>("workExtension");
    const property = parentProperties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.studentParent.parent?.workExtension}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={parentValidationErrors?.[propertyName]}
        value={props.studentParent.parent?.workExtension}
        onChange={(e) =>
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              draft.parent!.workExtension = e.target.value;
            })
          )
        }
      />
    );
  };

  const sameStudentAddressField = () => {
    const propertyName = pn<Parent>("sameStudentAddress");
    const property = parentProperties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {formatBoolean(props.studentParent.parent?.sameStudentAddress)}
      </DataDisplay>
    ) : (
      <Checkbox
        label={property?.displayName}
        checked={props.studentParent.parent?.sameStudentAddress}
        onChange={(e) =>
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              draft.parent!.sameStudentAddress = e.target.checked;
              if (e.target.checked) setAddressFromStudent(props.studentParent.studentId);
              else {
                draft.parent!.address = {} as Address;
              }
            })
          )
        }
      />
    );
  };

  const noteField = () => {
    const propertyName = pn<Parent>("note");
    const property = parentProperties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.studentParent.parent?.note}
      </DataDisplay>
    ) : (
      <Input
        multiline
        modelProperty={property}
        errors={parentValidationErrors?.[propertyName]}
        value={props.studentParent.parent?.note}
        onChange={(e) =>
          props.setStudentParent(
            produce(props.studentParent, (draft) => {
              draft.parent!.note = e.target.value;
            })
          )
        }
      />
    );
  };

  const renderParentOption = (parent: ParentAssignDto) => {
    const studentCount = parent.students?.length ?? 0;

    return (
      <Tooltip
        key={parent.parent?.id}
        title={
          studentCount > 0 ? (
            <Stack>
              <Typography variant="inherit">Students:</Typography>
              {parent.students?.map((student) => (
                <Typography key={student.id} variant="caption">
                  {student.lastName}, {student.firstName} ({student.id})
                </Typography>
              ))}
            </Stack>
          ) : undefined
        }
      >
        <ToggleButton
          value={parent.parent?.id!}
          sx={{ textTransform: "none", borderRadius: 2, px: 1.5, py: 1 }}
        >
          <Stack alignItems="flex-start" spacing="md">
            <Stack direction="row" alignItems="center" spacing="md">
              <Typography variant="body1" fontWeight={600}>
                {parent.parent?.lastName}, {parent.parent?.firstName}
              </Typography>
              <Chip label={`ID: ${parent.parent?.id}`} size="small" variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Assigned to {studentCount} {studentCount === 1 ? "student" : "students"}
            </Typography>
          </Stack>
        </ToggleButton>
      </Tooltip>
    );
  };

  return (
    <>
      <Grid ref={breakpointsRef} container spacing="sm">
        {relationDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            {relationField()}
          </Grid>
        )}

        <GridBreak />

        {props.studentParent && props.studentParent.id > 0 && parentIdDisplayField && (
          <Grid item xs={smallFieldBreakpoints}>
            <DataDisplay label={properties.find((x) => x.propertyName === pn<Parent>("id"))?.displayName}>
              {props.studentParent.parentId}
            </DataDisplay>
          </Grid>
        )}

        {!isAssigningParent && (
          <>
            <Grid item xs={smallFieldBreakpoints}>
              {firstNameField()}
            </Grid>

            <Grid item xs={smallFieldBreakpoints}>
              {lastNameField()}
            </Grid>
          </>
        )}

        <GridBreak />

        {assignableParents.length > 0 && (
          <Grid item>
            <Stack sx={{ backgroundColor: "action.hover", p: 2, borderRadius: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" fontWeight={600} color="primary">
                  Found Matching Parents
                </Typography>
                {isAssigningParent && (
                  <Tooltip title="Return to new parent">
                    <IconButton size="small" color="error" onClick={handleReturnToNewParent}>
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select an existing parent to use or create a new one:
              </Typography>
              <ToggleButtonGroup
                color="primary"
                exclusive
                value={props.studentParent.parentId}
                onChange={handleParentSelect}
                size="small"
                sx={{ flexWrap: "wrap", gap: 1, "& .MuiToggleButton-root": { m: 0 } }}
              >
                {assignableParents.map(renderParentOption)}
              </ToggleButtonGroup>
            </Stack>
          </Grid>
        )}
        <GridBreak />

        {!isAssigningParent && (
          <>
            {languageDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {languageField()}
              </Grid>
            )}

            {educationLevelDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {educationLevelField()}
              </Grid>
            )}

            <Grid item xs={smallFieldBreakpoints}>
              {emailField()}
            </Grid>

            {secondaryEmailDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {secondaryEmailField()}
              </Grid>
            )}

            {phoneNumberDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {phoneNumberField()}
              </Grid>
            )}

            {cellPhoneDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {cellPhoneField()}
              </Grid>
            )}

            {workPhoneDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {workPhoneField()}
              </Grid>
            )}

            {workExtensionDisplayField && (
              <Grid item xs={smallFieldBreakpoints}>
                {workExtensionField()}
              </Grid>
            )}

            <GridBreak />

            {addressDisplayField && (
              <>
                <Grid item xs={fullFieldBreakpoints}>
                  {sameStudentAddressField()}
                </Grid>

                <GridBreak />

                <Grid item xs={fullFieldBreakpoints}>
                  <AddressFields
                    crudMode={props.crudMode}
                    address={props.studentParent.parent?.address || ({} as Address)}
                    setAddress={(address) =>
                      props.setStudentParent(
                        produce(props.studentParent, (draft) => {
                          draft.parent!.address = address;
                        })
                      )
                    }
                    properties={parentProperties?.find((x) => x.propertyName === pn<Parent>("address"))?.children || []}
                    validationErrors={parentValidationErrors?.[pn<Parent>("address")] || []}
                  />
                </Grid>
              </>
            )}

            {noteDisplayField && (
              <Grid item xs={fullFieldBreakpoints}>
                {noteField()}
              </Grid>
            )}
          </>
        )}
      </Grid>
      {matchTemplatePath(parentTextingOptInPageRoute.templatePath) && <Outlet />}
    </>
  );
};
